import { useEffect, useState } from "react";
import { useLenis } from "lenis/react";
import { List, X } from "@phosphor-icons/react";
import { useLang } from "@/i18n";

const Logo = () => (
  <span className="flex items-center gap-3">
    <img src="/logo-mark.png" alt="Élite Service" className="h-10 w-auto" />
    <span className="hidden text-[12px] font-medium uppercase tracking-[0.35em] text-ivory sm:block">
      Élite Service
    </span>
  </span>
);

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const lenis = useLenis();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    lenis?.scrollTo(`#${id}`, { offset: -64, duration: 1.4 });
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500 ${
        scrolled ? "border-champagne/15 bg-navy/85 backdrop-blur-xl" : "border-transparent bg-navy/30 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-6 md:h-[72px] lg:px-12">
        <a href="#home" onClick={go("home")} data-testid="nav-logo" aria-label="Élite Service">
          <Logo />
        </a>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Main">
          {t.nav.links.slice(1).map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              onClick={go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="text-[12px] font-medium uppercase tracking-[0.18em] text-ivory/70 transition-colors duration-300 hover:text-champagne"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-6 lg:flex">
          <div className="flex items-center gap-1 text-[11px] font-semibold tracking-[0.2em]" data-testid="lang-switcher">
            <button
              data-testid="lang-toggle-it"
              onClick={() => setLang("it")}
              className={`px-1.5 py-1 transition-colors duration-300 ${lang === "it" ? "text-champagne" : "text-ivory/50 hover:text-ivory"}`}
            >
              IT
            </button>
            <span className="text-ivory/30">/</span>
            <button
              data-testid="lang-toggle-en"
              onClick={() => setLang("en")}
              className={`px-1.5 py-1 transition-colors duration-300 ${lang === "en" ? "text-champagne" : "text-ivory/50 hover:text-ivory"}`}
            >
              EN
            </button>
          </div>
          <a
            href="#contatti"
            onClick={go("contatti")}
            data-testid="nav-cta-button"
            className="border border-champagne/60 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-champagne transition-colors duration-300 hover:bg-champagne hover:text-navy"
          >
            {t.nav.cta}
          </a>
        </div>

        <button
          data-testid="mobile-menu-button"
          className="text-ivory lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={26} weight="light" /> : <List size={26} weight="light" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="border-t border-champagne/15 bg-navy/95 backdrop-blur-xl lg:hidden">
          <nav className="flex flex-col px-6 py-6">
            {(t.nav.links || []).map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={go(l.id)}
                data-testid={`mobile-nav-link-${l.id}`}
                className="border-b border-ivory/10 py-4 font-serif text-2xl text-ivory/85 transition-colors duration-300 hover:text-champagne"
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-3 pt-6 text-[12px] font-semibold tracking-[0.2em]">
              <button data-testid="mobile-lang-toggle-it" onClick={() => setLang("it")} className={lang === "it" ? "text-champagne" : "text-ivory/50"}>IT</button>
              <span className="text-ivory/30">/</span>
              <button data-testid="mobile-lang-toggle-en" onClick={() => setLang("en")} className={lang === "en" ? "text-champagne" : "text-ivory/50"}>EN</button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
